// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the link model
//  ---------------------------------------------------------------------------
//  Everything the editor needs to know about a link, with no DOM and no React:
//  what text counts as a URL, what is safe to open, and the single source edit
//  that writes one. md-markdown.jsx renders and md-compose.jsx hosts; the rules
//  live here so they can be asserted in a unit test rather than in a browser.
//
//  WHY THIS IS ITS OWN MODULE, AND ITS OWN TEST. md-markdown.jsx is 1,000 lines
//  of contentEditable and caret arithmetic that cannot be loaded without React
//  and a live selection, so anything living inside it is effectively untestable
//  today — and two of the three rules below are SECURITY decisions. A record body
//  is arbitrary text the writer may have pasted from anywhere, `[tap
//  me](javascript:…)` is perfectly valid markdown, and an editor that follows a
//  link follows whatever it is handed. mdLinkHref is the only thing between
//  those two facts, so it is the one piece that had to be provable.
//
//  THE SCHEME CHECK IS A PARSE, NOT A PREFIX TEST. `raw.startsWith('javascript:')`
//  is the obvious implementation and it is wrong: the URL parser strips leading
//  control characters and whitespace before it reads a scheme, and lowercases
//  what it finds — so a handwritten prefix test disagrees with the browser about
//  what the string means, and it is the browser that ends up running it. Parse
//  first, read the protocol the parser saw, allowlist that. Never a denylist:
//  a denylist is a promise that we have thought of every scheme, and we have not.
//
//  WHAT GETS AUTOLINKED IS DELIBERATELY NARROW. `https://…` and `www.…` only. A
//  bare dotted word is not evidence of a URL — prose is full of them ("node.js",
//  "e.g. tomorrow", "index.html", a missed space after a full stop) and every
//  one of those would light up accent-coloured and, on plain click, try to
//  navigate. An explicit `[label](mandana.app)` is different: the writer put a
//  target in a target slot, so mdLinkHref completes the scheme for it.
// ════════════════════════════════════════════════════════════════════════════

/** A bare URL found inside a run of plain text. `[start, end)` indexes that run;
    `href` is the resolved, already-safety-checked thing to open. */
interface MdLinkSpan {
  start: number;
  end: number;
  href: string;
}

/** The source rewrite a link command performs, in the same shape the editor's
    other appliers use: the whole new text, plus where the caret goes in it. */
interface MdLinkEdit {
  src: string;
  caret: number;
}

(function (g: any) {
  'use strict';

  /* The allowlist. http/https are the web; mailto is the one non-navigating
     scheme a journal actually uses. Everything else — data:, blob:, file:,
     vbscript:, intent:, and whatever ships next year — is refused. */
  const MD_SAFE_SCHEME = /^(?:https?|mailto):$/;

  /* Does this string already state a scheme? Matches the grammar the URL parser
     uses, not just the ones we allow, so `javascript:` reaches the parser and is
     rejected on protocol rather than being mistaken for a hostname and quietly
     rewritten to `https://javascript:alert(1)`. */
  const MD_HAS_SCHEME = /^[a-z][a-z0-9+.\-]*:/i;

  /* A scheme-less host: at least two dot-separated labels, optional port, and
     then either the end or the start of a path/query/fragment. Only ever applied
     to an explicit markdown target — never to prose. */
  const MD_BARE_HOST = /^[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?)+(?::\d+)?(?:[/?#]|$)/i;

  /** Resolve link target text to something safe to open, or null. */
  function mdLinkHref(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const s = raw.trim();
    if (!s) return null;
    const candidate = MD_HAS_SCHEME.test(s) ? s : MD_BARE_HOST.test(s) ? 'https://' + s : null;
    if (!candidate) return null;
    let u: URL;
    try { u = new URL(candidate); } catch (e) { return null; }
    if (!MD_SAFE_SCHEME.test(u.protocol)) return null;
    /* The PARSED form, not the writer's string. Whatever opens this then gets
       the same URL this function reasoned about, rather than re-parsing the raw
       text and possibly reaching a different answer. The normalisation it adds
       (a lowercased host, a trailing slash on a bare origin) is never displayed
       — the record always shows the source text. */
    return u.href;
  }

  /* Punctuation a sentence puts AFTER a URL, never inside one. Stripped from the
     tail so "read https://mandana.app." links the app and not the full stop. */
  const MD_TRAILING = /[.,;:!?'"“”‘’*_~]+$/;

  const countOf = (s: string, ch: string): number => {
    let n = 0;
    for (let i = 0; i < s.length; i++) if (s[i] === ch) n++;
    return n;
  };

  /* Trailing ")" is the one that cannot be decided by the character alone.
     Wikipedia's own URLs end in one — /wiki/Foo_(bar) — and so does a URL a
     writer wrapped in parentheses. Counting is what tells them apart: a closer
     with no opener to match inside the URL belongs to the sentence. */
  function mdTrimTrailing(url: string): string {
    let out = url;
    for (;;) {
      const cut = out.replace(MD_TRAILING, '');
      if (cut !== out) { out = cut; continue; }
      if (out.charAt(out.length - 1) === ')' && countOf(out, ')') > countOf(out, '(')) {
        out = out.slice(0, -1);
        continue;
      }
      return out;
    }
  }

  /* `<` and `>` end a match because they are the only characters that can never
     be inside a URL here and CAN be beside one — mdInline escapes them, and an
     autolink that swallowed one would put escaped markup inside the href. */
  const MD_SCAN = /(?:https?:\/\/|www\.)[^\s<>]+/gi;

  /** Find bare URLs in a run of plain text. Callers pass text that markdown has
      already had its turn at, so a URL inside `[…](…)` or a code span never
      reaches this — it was consumed before the run was cut. */
  function mdLinkScan(text: unknown): MdLinkSpan[] {
    if (typeof text !== 'string' || !text) return [];
    const out: MdLinkSpan[] = [];
    const re = new RegExp(MD_SCAN.source, 'gi');   // fresh: a shared /g/ regex carries lastIndex
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const whole = m[0];
      const url = mdTrimTrailing(whole);
      const href = mdLinkHref(url);
      /* Always step past the FULL match. Resuming inside the characters we just
         trimmed cannot find anything (they are punctuation), but a zero-width
         step on a pathological match would spin forever, and that is not a bug
         worth leaving available. */
      re.lastIndex = m.index + whole.length;
      if (!href) continue;                        // e.g. "https://..." — trimmed to nothing useful
      out.push({ start: m.index, end: m.index + url.length, href });
    }
    return out;
  }

  /** Write a link into the source. `[start, end)` is the writer's selection —
      empty when they only asked for a link, and the label when they had text
      picked out first.

      Returns null when the selection crosses a line break. A markdown link's
      label is a single line by construction, so there is no honest thing to do
      with three selected lines and guessing at one would silently mangle two of
      them. Doing nothing is a keystroke the writer can repeat; a wrong rewrite
      is a record they have to repair. */
  function mdLinkInsert(src: unknown, start: number, end: number): MdLinkEdit | null {
    if (typeof src !== 'string') return null;
    const a = Math.max(0, Math.min(start, src.length));
    const b = Math.max(a, Math.min(end, src.length));
    const label = src.slice(a, b);
    if (label.indexOf('\n') >= 0) return null;
    /* Where the caret lands is the whole ergonomics of this: with no label the
       next thing to type is the label, and with one already written the next
       thing is the URL. Both slots are opened and closed in one go, for the same
       reason mdApplyWrap does it — half-written syntax is what a writer is left
       to clean up otherwise. */
    return {
      src: src.slice(0, a) + '[' + label + ']()' + src.slice(b),
      caret: label ? b + 3 : a + 1,
    };
  }

  g.mdLinkHref = mdLinkHref;
  g.mdLinkScan = mdLinkScan;
  g.mdLinkInsert = mdLinkInsert;

  // CommonJS escape hatch, following md-overlap.js and md-merge.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = { mdLinkHref, mdLinkScan, mdLinkInsert };
})(typeof window !== 'undefined' ? window : globalThis);
